import { useQuery, useMutation, useQueryClient } from 'react-query'
import client, { multipartclient } from './api-client'
import { getAllUrlParamValues, getMetadataParamValues } from './utils'

const parcelQueryConfig = {
  staleTime: 1000 * 60 * 60,
  cacheTime: 1000 * 60 * 60,
}


async function fetchFarms(user, noParcels = false) {
  const params = new URLSearchParams();
  // Add all URL parameters to the params object
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.forEach((value, key) => {
    params.append(key, value);
  });
  // hectares_farms is set explicitly below for the list request only. Drop any
  // copy that came in from the address bar so it can't reach the detail call.
  params.delete('hectares_farms');
  // Add client_id to parameters
  params.append('client_id', user.id);
  // Check if generate_wms_link is present in params, if not, add it with value false
  if (!params.has('generate_wms_link')) {
    params.append('generate_wms_link', false);
  }
  // hectares_farms is a list-only flag: it makes the API skip parcels and return
  // per-resolution hectares. Keep it off `params`, which is reused below for the
  // detail request - that one needs parcels.
  const listParams = new URLSearchParams(params);
  if (noParcels) {
    listParams.append('hectares_farms', true);
  }
  const endpointUrl = `farms/?${listParams.toString()}`;
  const data = await client(endpointUrl);

  // Get farms from URL parameters
  const farms = getAllUrlParamValues('farms');

  // Filter farms based on the URL parameters
  const filteredFarms = data.filter((farm) =>
    farms.length > 0 ? farms.includes(farm.id.toString()) : true
  );

  // Handle the noParcels case
  if (noParcels && filteredFarms.length > 0) {
    const farmId = filteredFarms[0].id;
    const data2 = await client(`farms/${farmId}/?${params.toString()}`);
    // Merge data and data2 for the first farm
    filteredFarms[0] = { ...filteredFarms[0], ...data2 };
  }

  return filteredFarms;
}

export function useFarms(user, noParcels = false) {
  return useQuery(['farms', user.username], () => fetchFarms(user, noParcels), {
    refetchAllOnWindowFocus: false,
    ...parcelQueryConfig,
  })
}

async function fetchFarm(user, farmId) {
  const params = new URLSearchParams();

  // Add all URL parameters to the params object
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.forEach((value, key) => {
    params.append(key, value);
  });

  // Add client_id to parameters
  params.append('client_id', user.id);

  // Check if generate_wms_link is present in params, if not, add it with value false
  if (!params.has('generate_wms_link')) {
    params.append('generate_wms_link', false);
  }
  const data = await client(`farms/${farmId}/?${params.toString()}`);
  return data;
}

export function useFarm(user, farmId) {
  return useQuery(['farm', user.username, farmId], () => fetchFarm(user, farmId), {
    enabled: !!farmId && farmId !== -1, // don't fetch if farmId is -1
    refetchOnWindowFocus: false,
    ...parcelQueryConfig,
  });
}

async function addFarms(farmsData) {
  const data = await client('farms/', { body: farmsData })
  return data
}

export function useAddFarmsMutation(farmsData) {
  const queryClient = useQueryClient()

  return useMutation((farmsData) => addFarms(farmsData))
}


async function fetchParcelClassification(user, farmId, resolutionId, date, classificationDate) {
  const params = new URLSearchParams();

  // Add client_id to parameters
  params.append('client_id', user.id);
  // Add resolution ID
  params.append('resolution_id', resolutionId);
  // Add date
  if (date)
    params.append('date', date);
  // Add classification_date for harvest tracking multitemporal layers
  if (classificationDate)
    params.append('classification_date', classificationDate);

  const data = await client(`farms/${farmId}/parcel-classification/?${params.toString()}`);
  return data;
}

export function useParcelClassification(user, farmId, resolutionId, date, classificationDate) {
  return useQuery(['farm', user.username, farmId, resolutionId, date, classificationDate], () => fetchParcelClassification(user, farmId, resolutionId, date, classificationDate), {
    enabled: false,
    refetchOnWindowFocus: false,
    ...parcelQueryConfig,
  });
}


async function updateFarmTrees(files) {
  console.log(files)
  const data = await multipartclient(`plants/upload_farm_plants/`, { body: files });
  return data;
}

export function useUpdateFarmTreesMutation() {
  const queryClient = useQueryClient()

  return useMutation((files) => updateFarmTrees(files), {
    onSuccess: () => {
      // Invalidate plants queries to ensure fresh data after upload
      queryClient.invalidateQueries(['plants_stats'])
      queryClient.invalidateQueries(['plants'])
    }
  })
}

// New function to assess deforestation for a farm
async function fetchDeforestationAssessment(farmId, user) {
  const params = new URLSearchParams();
  
  // Add client_id to parameters
  params.append('client_id', user.id);
  
  const data = await client(`farms/${farmId}/assess_deforestation/?${params.toString()}`);
  return data;
}

export function useDeforestationAssessment(farmId, user) {
  return useQuery(
    ['deforestation-assessment', farmId, user?.id],
    () => fetchDeforestationAssessment(farmId, user),
    {
      enabled: !!farmId && !!user, // Only run query if farmId and user are provided
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
    }
  );
}

// Fetch all company farms with hectares for billing (company admins see all company farms)
async function fetchCompanyHectares(user) {
  const params = new URLSearchParams();
  params.append('client_id', user.id);

  const data = await client(`farms/company-hectares/?${params.toString()}`);
  return data;
}

export async function patchFarm(farmId, data) {
  return client(`farms/${farmId}/`, { body: data, method: 'PATCH' })
}

export function useUpdateFarmNameMutation() {
  return useMutation((editFarm) => patchFarm(editFarm.id, { name: editFarm.name }))
}

export function useCompanyHectares(user, options = {}) {
  return useQuery(
    ['company-hectares', user?.username],
    () => fetchCompanyHectares(user),
    {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 60,
      cacheTime: 1000 * 60 * 60,
      enabled: !!user && (options.enabled ?? true),
    }
  )
}
